import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  DatePicker,
  DatePickerProps,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import ErrorIcon from "@material-ui/icons/Error";
import {
  makeStyles,
  useTheme,
} from "@civicplus/preamble-ui/lib/Utilities/ThemeHelper";
import { TextField } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  formatDate,
  parseDate,
} from "@civicplus/preamble-ui/lib/Utilities/DateHelper";
import { usePrevious } from "@civicplus/preamble-ui/lib/Utilities/StateHelper";
import { ThemeProvider } from "@mui/material/styles";
import classNames from "classnames";

export const useStyles = makeStyles(theme => ({
  errorMessageWrapper: {
    display: "flex",
    flexGrow: 1,
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    margin: 0,
    padding: theme.spacing(1),
    paddingRight: theme.spacing(1.5),
    background: theme.palette.error.dark,
    color: theme.palette.background.paper,
    fontWeight: "bold",
    borderRadius: `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`,
  },
  errorIcon: {
    marginRight: theme.spacing(0.5),
  },
  errorHelperText: { "& .MuiFormHelperText-root": { margin: 0 } },
  dateFilterField: {
    width: "100%",
    "& .MuiFormLabel-root.MuiInputLabel-root.MuiInputLabel-shrink": {
      color: theme.palette.primary.main,
      fontWeight: "bold",
    },
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.palette.background.paper,
      "& fieldset": {
        borderRadius: theme.spacing(0.25),
        borderColor: theme.palette.action.disabled,
      },
    },
    "& .MuiOutlinedInput-input:-webkit-autofill": {
      "-webkit-box-shadow": `0 0 0 100px ${theme.palette.primary.main} inset`,
    },
    // Reenforcing MUI input styles for embeds.
    "& .MuiInputBase-input.MuiOutlinedInput-input.MuiInputBase-inputAdornedEnd":
      {
        boxSizing: "content-box",
      },
  },
  dateFilterActionBar: {
    "& button:first-child": { marginRight: "auto" },
  },
}));
interface IDatePickerWrapper
  extends Omit<DatePickerProps<Date, Date>, "onChange"> {
  error: boolean;
  errorMessage: string;
  executeValidation: (value: Date | null) => void;
  helperText: ReactNode;
  showMask: boolean;
  required: boolean;
  onChange: (
    value: { target: { value: Date | null } },
    date?: Date | null
  ) => void;
}

export const DatePickerWrapper = React.forwardRef<
  HTMLInputElement,
  IDatePickerWrapper
>(
  (
    {
      error,
      errorMessage,
      helperText,
      onChange,
      value,
      mask,
      executeValidation,
      showMask,
      required,
      inputFormat,
      ...otherProps
    },
    ref
  ) => {
    const theme = useTheme();
    const classes = useStyles();
    const [currentValue, setCurrentValue] = useState(value);
    const prevValue = usePrevious(currentValue);
    const refPicker = useRef<HTMLInputElement>(null);
    React.useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(
      ref,
      () => refPicker.current
    );

    useEffect(() => {
      setCurrentValue(value);
    }, [value]);

    const renderedErrorMessage = (
      <strong className={classes.errorMessageWrapper}>
        <ErrorIcon className={classes.errorIcon} />
        <span>{errorMessage}</span>
      </strong>
    );
    const messageText =
      error && errorMessage ? renderedErrorMessage : helperText;

    const tryCreateValidDate = useCallback(
      date => {
        const formattedDate = formatDate(
          date,
          inputFormat ? inputFormat : "M/d/yyyy"
        );
        return parseDate(formattedDate, inputFormat ? inputFormat : "M/d/yyyy");
      },
      [inputFormat]
    );

    const handleOnChange = (date: Date | null) => {
      // If regular date, set time to 00:00:00 so we check the full day in validations.
      if (date && date !== null) {
        date.setHours(0, 0, 0, 0);
      }
      setCurrentValue(date);
    };
    const handleValidDate = (date: Date | null) => {
      setCurrentValue(date);
      onChange({ target: { value: date } }, date);
    };
    const handleOnBlur = (
      event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>
    ) => {
      // If there is not mask, then we try to formatted the ourself
      const date = new Date(event.target.value);

      if (date && !isNaN(date.getTime())) {
        const parsedDate = tryCreateValidDate(date);
        if (parsedDate) {
          // If we was able to make a valid date/time, then we save it on the state and trigger the onChange
          setCurrentValue(parsedDate);
          onChange({ target: { value: date } }, parsedDate);
          return;
        }
      } else if (currentValue && !isNaN(currentValue.getTime())) {
        // currentValue is not null and has a valid date?
        // In case the formatting fail, we try to use the formatted object from the component
        onChange({ target: { value: date } }, currentValue);
        return;
      }
      if (error) {
        // We don't want to send a valid time value because it will throw an error
        // since the current value it not a valid date time object.
        const currentValue = refPicker?.current?.value
          ? new Date(refPicker?.current?.value)
          : null;
        setCurrentValue(currentValue);
        onChange({ target: { value: currentValue } }, currentValue);
      }
      // Need to execute validation here to check if the field is required after
      // leaving focus and it wasn't filled out.
      if (required && value == null && executeValidation) {
        executeValidation(null);
      }
    };
    const handleOnFocus = () => {
      if (error || errorMessage) {
        // When an invalid date is set as value, the date picker cannot be edited by keyboard.
        // This clears the value (setting to null), so that the user can type again.
        setCurrentValue(null);
        const dateValue = { target: { value: null } };
        onChange(dateValue, undefined);
        if (executeValidation) {
          executeValidation(currentValue);
        }
      }
    };

    useEffect(() => {
      if (typeof prevValue !== "undefined" && currentValue !== prevValue) {
        const dates = new Date(
          refPicker?.current?.value ? refPicker?.current?.value : ""
        );
        const parsedDates = tryCreateValidDate(dates);

        executeValidation(parsedDates || currentValue);
      }
    }, [currentValue, executeValidation, prevValue, tryCreateValidDate]);

    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <ThemeProvider theme={theme}>
          <DatePicker
            {...otherProps}
            closeOnSelect={false}
            showToolbar={false}
            PopperProps={{ placement: "bottom-start" }}
            value={currentValue ? currentValue : null}
            onChange={handleOnChange}
            onAccept={handleValidDate}
            inputRef={refPicker}
            views={["year", "month", "day"]}
            componentsProps={{
              actionBar: {
                className: classes.dateFilterActionBar,
                actions: ["clear", "cancel", "accept"],
              },
            }}
            renderInput={props => (
              <TextField
                {...props}
                className={classNames(
                  classes.errorHelperText,
                  classes.dateFilterField
                )}
                inputProps={{
                  ...props.inputProps,
                  "aria-label":
                    props.label !== null ? props.label?.toString() : undefined,
                }}
                placeholder="mm/dd/yyy"
                helperText={messageText}
                onBlur={handleOnBlur}
                onFocus={handleOnFocus}
              />
            )}
          />
        </ThemeProvider>
      </LocalizationProvider>
    );
  }
);
DatePickerWrapper.displayName = "DatePickerWrapper";

export default DatePickerWrapper;
